import { useSnackbar } from 'notistack';
import { parse as parseQuery, stringify as stringifyQuery } from 'query-string';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _pick from 'lodash/pick';
import { FounderId } from '../api/founders';
import { createCheckin, searchGoals } from '../api/goals';
import { MentorId } from '../api/mentors';
import { Goal, GoalId, GoalStatus } from '../api/types/Goal';
import { Role } from '../api/user/Role';
import venturesAPI from '../api/ventures';
import { Venture, VentureId } from '../api/ventures/types/Venture';
import { SnackMessage } from '../components/common';
import {
  GoalsFilters,
  GoalsFilterValue,
} from '../components/tables/goals-table';
import { massAsyncRequest } from '../utils/api';
import { decodeQuery, encodeQuery, isMobile } from '../utils/functions';
import { UserContext } from './user-context';

interface GoalsProviderProps {
  children: ReactNode;
}

export interface GoalsContextProps {
  goals: Goal[];
  filters: GoalsFilters;
  page: number;
  ventures: Venture[];
  isVenturesLoading: boolean;
  ventureOptions: { value: string; label: string }[];
  isLoading: boolean;
  isPageLoading: boolean;
  hasMorePages: boolean;
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  loadError: any;
  hasActiveFilters: boolean;
  showClearFilter: boolean;
  searchValue: string;
  selectedGoalIds: GoalId[];
  handleSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClearSearch: () => void;
  handleFilterUpdate: (field: string, value: GoalsFilterValue) => void;
  handleFiltersUpdate: (newFilters: GoalsFilters) => void;
  handlePageChange: (nextPage: number) => void;
  handleNextPage: () => void;
  handleClearFilters: () => void;
  handleGoalSliderChange: (onChangeProps: {
    id: string;
    progress: number;
    status: GoalStatus;
  }) => void;
  setSelectedGoalIds: Dispatch<SetStateAction<GoalId[]>>;
  handleBulkArchive: (goalIds: GoalId[]) => Promise<void>;
  handleBulkUnarchive: (goalIds: GoalId[]) => Promise<void>;
  reloadGoals: () => Promise<void>;
}

const defaultFilters: GoalsFilters = {
  status: 'ALL',
  venture: undefined,
  search: undefined,
};

const getInitialFilters = (filter: string | null): GoalsFilters => {
  if (filter) {
    const decodedFilter = decodeQuery(filter);
    return { ...defaultFilters, ...decodedFilter };
  } else {
    return defaultFilters;
  }
};

const getPageFromURL = (search: string): number => {
  const parsedQuery = _pick(parseQuery(search), ['page']);
  return parsedQuery.page ? Number(parsedQuery.page) : 1;
};

export const GoalsContext = createContext<GoalsContextProps>({
  goals: [],
  filters: defaultFilters,
  page: 1,
  ventures: [],
  isVenturesLoading: true,
  ventureOptions: [],
  isLoading: true,
  isPageLoading: false,
  hasMorePages: true,
  hasNextPage: false,
  isNextPageLoading: false,
  loadError: undefined,
  hasActiveFilters: false,
  showClearFilter: false,
  searchValue: '',
  selectedGoalIds: [],
  handleSearch: () => { },
  handleClearSearch: () => { },
  handleFilterUpdate: () => { },
  handleFiltersUpdate: () => { },
  handlePageChange: () => { },
  handleNextPage: () => { },
  handleClearFilters: () => { },
  handleGoalSliderChange: () => { },
  setSelectedGoalIds: () => { },
  handleBulkArchive: async () => { },
  handleBulkUnarchive: async () => { },
  reloadGoals: async () => { },
});

export const GoalsProvider = ({ children }: GoalsProviderProps) => {
  const location = useLocation();
  const history = useHistory();
  const { hasRole, identityid } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const query = useMemo(() => parseQuery(location.search), [location.search]);

  const [goals, setGoals] = useState<Goal[]>([]);
  const [filters, setFilters] = useState<GoalsFilters>(
    getInitialFilters(query.filter as string | null),
  );
  const page = useMemo(
    () => getPageFromURL(location.search),
    [location.search],
  );
  const [ventures, setVentures] = useState<Venture[]>([]);
  const [isVenturesLoading, setIsVenturesLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [loadError, setLoadError] = useState<any>();
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedGoalIds, setSelectedGoalIds] = useState<GoalId[]>([]);
  const previousIdentityId = useRef<string | undefined>();
  const prevFiltersRef = useRef<GoalsFilters | null>(null);
  const prevPageRef = useRef<number | null>(null);
  const isInitialLoadRef = useRef(true);
  const debouncedTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isNextPageLoadingRef = useRef(false);

  const ventureOptions = useMemo(
    () => [
      { value: 'ALL', label: 'All Ventures' },
      ...ventures.map((fv) => ({ value: fv.id, label: fv.ventureName })),
    ],
    [ventures],
  );

  const handleFiltersUpdate = useCallback(
    (newFilters: GoalsFilters) => {
      setFilters(newFilters);
      history.replace(
        `${location.pathname}?${stringifyQuery({
          filter: encodeQuery(newFilters),
          page: 1,
        })}`,
      );
    },
    [history, location.pathname],
  );

  const handleFilterUpdate = useCallback(
    (field: string, value: GoalsFilterValue) => {
      handleFiltersUpdate({
        ...filters,
        [field]: value,
      });
    },
    [filters, handleFiltersUpdate],
  );

  const handleGoalSliderChange = useCallback(
    ({
      id,
      progress,
      status,
    }: {
      id: string;
      progress: number;
      status: GoalStatus;
    }) => {
      setGoals((prev) =>
        prev.map((prevGoal) =>
          prevGoal.id === id ? { ...prevGoal, progress, status } : prevGoal,
        ),
      );
    },
    [],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      const queryParams = parseQuery(location.search);
      const newQuery = stringifyQuery({
        ...queryParams,
        filter: encodeQuery(filters),
        page: nextPage > 1 ? nextPage : undefined,
      });

      if (newQuery) {
        history.replace(`${location.pathname}?${newQuery}`);
      } else {
        history.replace(location.pathname);
      }
    },
    [filters, location.search, location.pathname, history],
  );

  const handleNextPage = useCallback(() => {
    if (!hasMorePages || isPageLoading || isNextPageLoadingRef.current) return;
    isNextPageLoadingRef.current = true;
    handlePageChange(page + 1);
    // Reset flag after a short delay to allow page change to process
    setTimeout(() => {
      isNextPageLoadingRef.current = false;
    }, 300);
  }, [hasMorePages, isPageLoading, page, handlePageChange]);

  const handleClearFilters = useCallback(() => {
    handleFiltersUpdate(defaultFilters);
  }, [handleFiltersUpdate]);

  // Check next page for desktop table pagination
  const checkNextPage = useCallback(
    async (currentPage: number, loadedGoals: Goal[]) => {
      const isDesktopTable = !isMobile();
      if (!isDesktopTable || loadedGoals.length !== 10) {
        setHasNextPage(false);
        setIsNextPageLoading(false);
        return;
      }

      setIsNextPageLoading(true);
      try {
        const nextGoals = await searchGoals({
          search: filters.search,
          ventureId: filters.venture?.id as VentureId | undefined,
          status: filters.status !== 'ALL' ? filters.status : undefined,
          pageNum: currentPage + 1,
        });
        setHasNextPage(nextGoals.length > 0);
      } catch {
        setHasNextPage(false);
      } finally {
        setIsNextPageLoading(false);
      }
    },
    [filters.search, filters.status, filters.venture],
  );

  const reloadGoals = useCallback(async () => {
    const searchParams = {
      search: filters.search,
      ventureId: filters.venture?.id as VentureId | undefined,
      status: filters.status !== 'ALL' ? filters.status : undefined,
    };
    const isMobileList = isMobile();

    try {
      if (isMobileList) {
        const pages = Array.from({ length: page > 1 ? page : 1 }, (_, i) => i);
        const promises = pages.map((p) =>
          searchGoals({
            ...searchParams,
            pageNum: p,
          }),
        );
        const loadedGoalsResult = await Promise.all(promises);
        const loadedGoals = loadedGoalsResult.flat();
        setGoals(loadedGoals);
        setHasMorePages(loadedGoals.length > 0);
      } else {
        const currentPage = page > 0 ? page - 1 : 0;
        const loadedGoals = await searchGoals({
          ...searchParams,
          pageNum: currentPage,
        });

        // If no goals are found and we are not on the first page, go back to the previous page
        if (loadedGoals.length === 0 && page > 1) {
          handlePageChange(page - 1);
          return;
        }

        setGoals(loadedGoals);
        await checkNextPage(currentPage, loadedGoals);
      }
    } catch (error: any) {
      if (error?.response?.status !== 404 && error?.message !== '404') {
        setLoadError(error?.response?.data?.message || 'Internal server error');
      } else {
        setGoals([]);
        setHasNextPage(false);
      }
    }
  }, [filters, page, checkNextPage, handlePageChange]);

  const handleBulkArchive = useCallback(
    async (goalIds: GoalId[]) => {
      try {
        const goalsToArchive = goals.filter((g) => goalIds.includes(g.id));
        const archiveFunctions = goalsToArchive.map(
          (goal) => () =>
            createCheckin({
              goalId: goal.id,
              progress: goal.progress,
              status: 'ARCHIVED',
              contents: '',
            }),
        );
        await massAsyncRequest(archiveFunctions);
        setSelectedGoalIds([]);
        await reloadGoals();
        enqueueSnackbar('The goals were successfully archived.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e?.response?.data?.message;
        enqueueSnackbar(
          'An error occurred while archiving goals. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
      }
    },
    [goals, enqueueSnackbar, reloadGoals],
  );

  const handleBulkUnarchive = useCallback(
    async (goalIds: GoalId[]) => {
      try {
        const goalsToUnarchive = goals.filter((g) => goalIds.includes(g.id));
        const unarchiveFunctions = goalsToUnarchive.map(
          (goal) => () =>
            createCheckin({
              goalId: goal.id,
              progress: goal.progress,
              status: 'NOT_STARTED',
              contents: '',
            }),
        );
        await massAsyncRequest(unarchiveFunctions);
        setSelectedGoalIds([]);
        await reloadGoals();
        enqueueSnackbar('The goals were successfully unarchived.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e?.response?.data?.message;
        enqueueSnackbar(
          'An error occurred while unarchiving goals. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
      }
    },
    [goals, enqueueSnackbar, reloadGoals],
  );

  const hasActiveFilters = useMemo(
    () => filters.status !== 'ALL' || !!filters.venture,
    [filters.status, filters.venture],
  );

  const showClearFilter = useMemo(
    () => filters.status !== 'ALL' || !!filters.venture || !!filters.search,
    [filters.status, filters.venture, filters.search],
  );

  const loadVentures = useCallback(
    async (currentIdentityId: string) => {
      try {
        setIsVenturesLoading(true);
        let loadedVentures: Venture[] = [];

        if (hasRole(Role.Mentor)) {
          loadedVentures = await venturesAPI.getVenturesDetailsByMentorId(
            currentIdentityId as MentorId,
          );
        } else if (hasRole(Role.Founder)) {
          loadedVentures = await venturesAPI.getVenturesDetailsByFounderId(
            currentIdentityId as FounderId,
          );
        }

        setVentures(loadedVentures);
        setIsVenturesLoading(false);
      } catch (error) {
        setIsVenturesLoading(false);
        console.error('Error loading ventures:', error);
      }
    },
    [hasRole],
  );

  useEffect(() => {
    // Only reload ventures if identityid actually changed
    if (previousIdentityId.current === identityid) {
      return;
    }
    previousIdentityId.current = identityid;

    if (identityid && (hasRole(Role.Mentor) || hasRole(Role.Founder))) {
      loadVentures(identityid);
    } else {
      // For admins/managers, don't load ventures (they use SelectVenturePopover)
      setVentures([]);
      setIsVenturesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityid]);

  // Sync filters and search value from URL when it changes
  useEffect(() => {
    const filterFromURL = getInitialFilters(query.filter as string | null);
    setFilters(filterFromURL);
    setSearchValue(filterFromURL.search || '');
  }, [query.filter]);

  // Debounced search handler
  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);

      if (debouncedTimerRef.current) {
        clearTimeout(debouncedTimerRef.current);
      }

      debouncedTimerRef.current = setTimeout(() => {
        if (value.trim().length > 2) {
          handleFilterUpdate('search', value.trim());
        } else {
          handleFilterUpdate('search', undefined);
        }
      }, 800);
    },
    [handleFilterUpdate],
  );

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    handleFilterUpdate('search', undefined);
  }, [handleFilterUpdate]);

  // Fetch goals when filters or page changes
  useEffect(() => {
    const filtersChanged =
      !prevFiltersRef.current ||
      prevFiltersRef.current.search !== filters.search ||
      prevFiltersRef.current.status !== filters.status ||
      prevFiltersRef.current.venture?.id !== filters.venture?.id;
    const pageChanged =
      prevPageRef.current !== null && prevPageRef.current !== page;
    const isInitialLoad = isInitialLoadRef.current;
    const isMobileList = isMobile();
    const isDesktopTable = !isMobile();

    // Initialize refs on first render
    if (isInitialLoad) {
      prevFiltersRef.current = filters;
      prevPageRef.current = page;
      isInitialLoadRef.current = false;
    }

    const loadGoals = async () => {
      const searchParams = {
        search: filters.search,
        ventureId: filters.venture?.id as VentureId | undefined,
        status: filters.status !== 'ALL' ? filters.status : undefined,
      };

      // Initial load or filters changed: reset and load pages
      if (isInitialLoad || filtersChanged) {
        prevFiltersRef.current = filters;
        setIsLoading(true);
        setLoadError(undefined);
        setGoals([]);
        setHasMorePages(true);
        setSelectedGoalIds([]);

        try {
          if (isMobileList) {
            const pages = Array.from(
              { length: page > 1 ? page : 1 },
              (_, i) => i,
            );
            const promises = pages.map((p) =>
              searchGoals({
                ...searchParams,
                pageNum: p,
              }),
            );
            const loadedGoalsResult = await Promise.all(promises);
            const loadedGoals = loadedGoalsResult.flat();
            setGoals(loadedGoals);
            setHasMorePages(loadedGoals.length > 0);
          } else {
            // Desktop: load only current page
            const currentPage = page > 0 ? page - 1 : 0;
            const loadedGoals = await searchGoals({
              ...searchParams,
              pageNum: currentPage,
            });
            setGoals(loadedGoals);
            await checkNextPage(currentPage, loadedGoals);
          }
          setIsLoading(false);
        } catch (error: any) {
          setIsLoading(false);
          if (error?.response?.status !== 404 && error?.message !== '404') {
            setLoadError(
              error?.response?.data?.message || 'Internal server error',
            );
          } else {
            setGoals([]);
            setHasNextPage(false);
          }
        }
        return;
      }

      // Page changed with unchanged filters: load next page (mobile) or reload (desktop)
      if (pageChanged && !isLoading) {
        prevPageRef.current = page;

        if (isMobileList && page > 1) {
          // Mobile: infinite scroll - append next page
          setIsPageLoading(true);
          try {
            const loadedGoals = await searchGoals({
              ...searchParams,
              pageNum: page - 1,
            });

            if (loadedGoals.length === 0) {
              setHasMorePages(false);
            }

            setGoals((prev) => [...prev, ...loadedGoals]);
            requestAnimationFrame(() => setIsPageLoading(false));
          } catch (error: any) {
            setIsPageLoading(false);
            if (error?.response?.status === 404 || error?.message === '404') {
              setHasMorePages(false);
            } else {
              console.error('Error loading goals page:', error);
            }
          }
        } else if (isDesktopTable) {
          // Desktop: reload current page
          setIsLoading(true);
          setLoadError(undefined);
          try {
            const currentPage = page - 1;
            const loadedGoals = await searchGoals({
              ...searchParams,
              pageNum: currentPage,
            });

            setGoals(loadedGoals);
            setIsLoading(false);
            await checkNextPage(currentPage, loadedGoals);
          } catch (error: any) {
            setIsLoading(false);
            if (error?.response?.status !== 404 && error?.message !== '404') {
              setLoadError(
                error?.response?.data?.message || 'Internal server error',
              );
            } else {
              setGoals([]);
              setHasNextPage(false);
            }
          }
        }
      }
    };

    loadGoals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.venture, page, checkNextPage]);

  const contextValue: GoalsContextProps = {
    goals,
    filters,
    page,
    ventures,
    isVenturesLoading,
    ventureOptions,
    isLoading,
    isPageLoading,
    hasMorePages,
    hasNextPage,
    isNextPageLoading,
    loadError,
    hasActiveFilters,
    showClearFilter,
    searchValue,
    selectedGoalIds,
    handleSearch,
    handleClearSearch,
    handleFilterUpdate,
    handleFiltersUpdate,
    handlePageChange,
    handleNextPage,
    handleClearFilters,
    handleGoalSliderChange,
    setSelectedGoalIds,
    handleBulkArchive,
    handleBulkUnarchive,
    reloadGoals,
  };

  return (
    <GoalsContext.Provider value={contextValue}>
      {children}
    </GoalsContext.Provider>
  );
};

export const useGoalsContext = () => {
  const context = useContext(GoalsContext);
  if (!context) {
    throw new Error('useGoalsContext must be used within a GoalsProvider');
  }
  return context;
};
